/**
 * File: apps/qbench-cp/reactapp/src/shared/constants/fields.js
 *
 * Purpose: Defines constants for additional/custom field types, field-related groupings, and
 *     module/permission flags used across the customer portal.
 *
 * Scope: Field type enums, per-entity field configuration limits, column sort direction, feature
 *     module flags, and portal-view permission keys shared throughout the customer portal.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: Exports `AddlFieldType` (the set of custom field data types such as TEXT, DATE, TAG,
 *     SELECT, OBJECT_RELATION), grouping arrays like `SelectBasedFields` and `MultiFields` that
 *     identify which field types render via react-select, and `ReadOnlyFieldsByEntity` /
 *     `TagFieldNames` for entity-specific field behavior. Also defines `ColumnSort`, numeric
 *     limits controlling how many custom columns/fields render per entity type, `ENABLED_MODULES`
 *     and `PORTALVIEW_PERMISSION` maps that translate feature flag names to their backend
 *     permission keys, `INVOICE_LINE_ITEM_TYPES`, and a `SETTINGS` map of miscellaneous toggle
 *     keys.
 *
 * Exports: AddlFieldType, AddlSelectEntityReference, SelectBasedFields, MultiFields,
 *     ReadOnlyFieldsByEntity, TagFieldNames, ColumnSort, NUMBER_OF_COLUMNS_FOR_SAMPLE and related
 *     column-count constants, ENABLED_MODULES, PORTALVIEW_PERMISSION, INVOICE_LINE_ITEM_TYPES,
 *     SETTINGS.
 */

import { EntityType } from './entityTypes';

export const AddlFieldType = {
	NUM: 'NUM',
	STR: 'STR',
	TEXT: 'TEXT',
	DATE: 'DATE',
	TAG: 'TAG',
	DATETIME: 'DATETIME',
	BOOL: 'BOOL',
	SELECT: 'SELECT',
	SELECT_MULTI: 'SELECT_MULTI',
	HYPERLINK: 'HYPERLINK',
	OBJECT_RELATION: 'OBJECT_RELATION',
	OBJECT_RELATION_MULTI: 'OBJECT_RELATION_MULTI',
	RICH_TEXT: 'RICH_TEXT',
	DUMMY: 'DUMMY',
};

export const AddlSelectEntityReference = {
	[EntityType.CONTACT]: 'CONTACT',
	[EntityType.USER]: 'USER',
};

// These are fields that use react-select under the hood.
export const SelectBasedFields = [
	AddlFieldType.TAG,
	AddlFieldType.SELECT,
	AddlFieldType.SELECT_MULTI,
	AddlFieldType.OBJECT_RELATION,
	AddlFieldType.OBJECT_RELATION_MULTI,
];

export const MultiFields = [
	AddlFieldType.SELECT_MULTI,
	AddlFieldType.OBJECT_RELATION_MULTI,
];

export const ReadOnlyFieldsByEntity = {
	[EntityType.CONTACT]: ['email'],
	[EntityType.CUSTOMER]: ['qbd_id', 'qbd_edit_sequence', 'qbd_last_synced', 'qbd_response_log'],
};

export const TagFieldNames = {
	TAGS: 'tags',
	EMAIL_TO: 'email_to',
	SUBMITTED_BY: 'submitted_by',
};

export const ColumnSort = {
	DESC: 'desc',
	ASC: 'asc',
};

export const NUMBER_OF_COLUMNS_FOR_SAMPLE = 6;
export const NUMBER_OF_COLUMNS_FOR_SAMPLE_MOBILE = 2;
export const NUMBER_OF_COLUMNS_FOR_TEST = 5;
export const NUMBER_OF_COLUMNS_FOR_ASSAY = 5;

export const NUMBER_OF_CUSTOM_COLUMNS_ORDER_ADDITIONAL_INFO = 4;
export const NUMBER_OF_CUSTOM_COLUMNS_ORDER_ADDITIONAL_INFO_MOBILE = 2;
export const NUMBER_OF_ORDERS_FIELDS = 8;
export const NUMBER_OF_ORDER_LIST_FIELDS = 3;

export const NUMBER_OF_CUSTOM_COLUMNS_SAMPLE_ADDITIONAL_INFO = 4;
export const NUMBER_OF_SAMPLES_FIELDS = 8;

export const ENABLED_MODULES = {
	BILLING: 'BILLING_ENABLED',
	TURNAROUNDS: 'TURNAROUNDS_ENABLED',
	EDD: 'EDD_ENABLED',
	SOURCES: 'SOURCES_ENABLED',
	PROJECTS: 'TRIALS_ENABLED',
	DIVISIONS: 'DIVISIONS_ENABLED',
	CPV2_SOURCE_IMPORT: 'CPV2_SOURCE_IMPORT_ENABLED',
};

export const PORTALVIEW_PERMISSION = {
	COMMENT_VIEW: 'COMMENT_VIEW',
	COMMENT_WRITE: 'COMMENT_WRITE',
	PRICING_VIEW: 'DISABLE_PORTAL_PRICING',
	PROJECT_VIEW: 'TRIAL',
	ACCOUNT_CREATE: 'ALLOW_CUSTOMER_SIGNUP',
	ORDER_REQUEST_CREATE: 'ORDER_REQUEST',
	ORDER_REQUEST_CLONE: 'ORDER_REQUEST_CLONE',
	TEST_RECURRENCE_CREATE: 'ORDER_REQUEST_TEST_RECURRENCE',
	TEST_FIELDS_EXPAND: 'ORDER_REQUEST_TEST_AUTO_EXPAND',
	SAMPLE_ASSAY_ENFORCEMENT: 'ORDER_REQUEST_FORCE_SAMPLE_ASSAY_ASSIGNMENT',
	KEEP_OVERRIDDEN_REPORTS: 'keep_overridden_reports_in_portal',
	INDICATE_OVERRIDDEN_REPORTS: 'indicate_override_reports',
	ASSAY_LEVEL_EDITING: 'ORDER_REQUEST_ASSAY_LEVEL_EDITING',
};

export const INVOICE_LINE_ITEM_TYPES = {
	INVOICE_ITEM: 'INVOICE_ITEM',
	SUBTOTAL: 'SUBTOTAL',
	FLAT_SURCHARGE: 'FLAT_SURCHARGE',
	FLAT_DISCOUNT: 'FLAT_DISCOUNT',
	PERCENT_SURCHARGE: 'PERCENT_SURCHARGE',
	PERCENT_DISCOUNT: 'PERCENT_DISCOUNT',
	TOTAL: 'TOTAL', // Note: This type doesn't exist on the LIMS
};

export const SETTINGS = {
	FILTER_TAT_BY_ASSAY_ENABLED: 'FILTER_TAT_BY_ASSAY_ENABLED',
	INVOICE_GROUP_ITEMS_BY_PANEL: 'INVOICE_GROUP_ITEMS_BY_PANEL',
	ORDER_TAT_TO_TEST_ENABLED: 'ORDER_TAT_TO_TEST_ENABLED',
};
